/** Runtime environment helpers (VITE_APP_ENV). */

import { isNativePlatform } from '@/utils/platform'

/** Emails allowed to open the Inbox Agent Dev Harness outside local development. */
export const DEV_HARNESS_ALLOWED_EMAILS = new Set([
    'kevin@joinlighthouse.ai',
    'ryan@ryansfigueroa.com',
    'ryan@joinlighthouse.ai',
])

export function getAppEnv(): string {
    return import.meta.env.VITE_APP_ENV ?? 'development'
}

export function isDevelopmentEnv(): boolean {
    return getAppEnv() === 'development'
}

/**
 * Whether the signed-in user may access `/dev/testing`.
 * Local development: any authenticated user. Elsewhere: admin allowlist only.
 * Must stay in sync with `_HARNESS_ALLOWED_EMAILS` in the backend harness.
 */
export function canAccessDevHarness(email: string | null | undefined): boolean {
    if (isDevelopmentEnv()) return true
    if (!email) return false
    return DEV_HARNESS_ALLOWED_EMAILS.has(email.trim().toLowerCase())
}

/** After sign-in in production/staging, these users open the harness instead of Home. */
const DEV_HARNESS_SIGN_IN_EMAILS = new Set([
    'kevin@joinlighthouse.ai',
])

export function shouldLandOnDevHarness(email: string | null | undefined): boolean {
    if (isNativePlatform()) return false
    if (!isProductionEnv() && !isStagingEnv()) return false
    if (!email) return false
    return DEV_HARNESS_SIGN_IN_EMAILS.has(email.trim().toLowerCase())
}

export function signedInHomeRoute(email: string | null | undefined): { name: 'dev-testing' | 'dashboard' } {
    return shouldLandOnDevHarness(email) ? { name: 'dev-testing' } : { name: 'dashboard' }
}

export function isDemoEnv(): boolean {
    if (getAppEnv() === 'demo') return true
    if (typeof window === 'undefined') return false
    return /^demo\./i.test(window.location.hostname)
}

export function isStagingEnv(): boolean {
    return getAppEnv() === 'staging'
}

export function isProductionEnv(): boolean {
    return getAppEnv() === 'production'
}

/**
 * Production and staging share the same app behavior; only env vars differ.
 * Demo is included for historical parity with backend ``is_production_like``.
 * Staging uses Stripe test-mode keys and the same checkout / subscription gate
 * as production.
 */
export function isProductionLikeEnv(): boolean {
    const env = getAppEnv()
    return env === 'production' || env === 'staging' || env === 'demo'
}

/**
 * When true, the UI suppresses direct send API calls and inbox Gmail sync
 * refresh. Pair with backend ``DISABLE_MAIL=true`` so buffered/scheduled sends
 * also skip provider transmission while DB side effects still run.
 *
 * Omitted at build time (typical production) → false; normal send behavior.
 */
export function isMailDisabled(): boolean {
    return (import.meta.env.VITE_APP_DISABLE_MAIL ?? '') === 'true'
}

